import type { TextParagraph } from '../model';
import { blockOffset, locate, textLength } from '../domain/richText';
import { DEFAULT_TEXT_FONT_FAMILY, DEFAULT_TEXT_FONT_SIZE_PT, PX_PER_POINT } from '../rendering/textLayout';

// #756 -- the inline editor's DOM is rebuilt from the run model after every edit (never parsed
// back): one <div data-paragraph> per paragraph, one <span> per run, text set via textContent so
// authored text can never become markup. Sizes match the paper: points -> px, times viewport zoom.
export function renderParagraphsInto(root: HTMLElement, paragraphs: readonly TextParagraph[], zoom: number): void {
  const doc = root.ownerDocument;
  root.replaceChildren(
    ...paragraphs.map((paragraph) => {
      const line = doc.createElement('div');
      line.dataset['paragraph'] = '';
      line.style.textAlign = paragraph.align ?? 'left';
      if (paragraph.runs.length === 0) line.appendChild(doc.createElement('br'));
      for (const run of paragraph.runs) {
        const span = doc.createElement('span');
        span.textContent = run.text;
        const sizePx = (run.fontSize ?? DEFAULT_TEXT_FONT_SIZE_PT) * PX_PER_POINT * zoom;
        span.style.fontFamily = run.fontFamily ?? DEFAULT_TEXT_FONT_FAMILY;
        span.style.fontSize = `${run.script ? sizePx * 0.7 : sizePx}px`;
        span.style.fontWeight = run.bold ? '700' : '400';
        span.style.fontStyle = run.italic ? 'italic' : 'normal';
        if (run.script) span.style.verticalAlign = run.script === 'sub' ? 'sub' : 'super';
        line.appendChild(span);
      }
      return line;
    })
  );
}

function paragraphElements(root: HTMLElement): HTMLElement[] {
  return Array.from(root.children).filter((child): child is HTMLElement => child instanceof HTMLElement);
}

// Characters inside `container` that precede (node, nodeOffset).
function textBefore(container: Node, node: Node, nodeOffset: number): number {
  if (node.nodeType === Node.TEXT_NODE) {
    let total = nodeOffset;
    const walker = container.ownerDocument!.createTreeWalker(container, NodeFilter.SHOW_TEXT);
    for (let current = walker.nextNode(); current && current !== node; current = walker.nextNode()) {
      total += current.textContent?.length ?? 0;
    }
    return total;
  }
  // An element position counts the text of its first `nodeOffset` children, plus whatever precedes
  // the element itself inside `container`.
  let total = 0;
  for (let i = 0; i < nodeOffset && i < node.childNodes.length; i++)
    total += node.childNodes[i]!.textContent?.length ?? 0;
  if (node === container) return total;
  const walker = container.ownerDocument!.createTreeWalker(container, NodeFilter.SHOW_TEXT);
  for (let current = walker.nextNode(); current; current = walker.nextNode()) {
    if (node.compareDocumentPosition(current) & Node.DOCUMENT_POSITION_PRECEDING)
      total += current.textContent?.length ?? 0;
  }
  return total;
}

// The block offset of a DOM position inside the editor, or null when it lies outside it.
export function domPositionToOffset(
  root: HTMLElement,
  paragraphs: readonly TextParagraph[],
  node: Node,
  nodeOffset: number
): number | null {
  if (node === root) {
    // A position between paragraph elements: the start of the next one, or the very end.
    return nodeOffset >= paragraphs.length ? textLength(paragraphs) : blockOffset(paragraphs, nodeOffset, 0);
  }
  if (!root.contains(node)) return null;
  const lines = paragraphElements(root);
  const index = lines.findIndex((line) => line === node || line.contains(node));
  if (index < 0) return null;
  const local = textBefore(lines[index]!, node, nodeOffset);
  const length = paragraphs[index]?.runs.reduce((sum, run) => sum + run.text.length, 0) ?? 0;
  return blockOffset(paragraphs, index, Math.min(local, length));
}

export function offsetToDomPosition(
  root: HTMLElement,
  paragraphs: readonly TextParagraph[],
  offset: number
): { readonly node: Node; readonly offset: number } {
  const { index, offset: local } = locate(paragraphs, offset);
  const line = paragraphElements(root)[index];
  if (!line) return { node: root, offset: root.childNodes.length };
  const walker = root.ownerDocument.createTreeWalker(line, NodeFilter.SHOW_TEXT);
  let remaining = local;
  let lastText: Node | null = null;
  for (let current = walker.nextNode(); current; current = walker.nextNode()) {
    const length = current.textContent?.length ?? 0;
    // Prefer the end of the earlier run at a boundary, so typing continues that run's format.
    if (remaining <= length) return { node: current, offset: remaining };
    remaining -= length;
    lastText = current;
  }
  return lastText ? { node: lastText, offset: lastText.textContent?.length ?? 0 } : { node: line, offset: 0 };
}

export function readSelection(
  root: HTMLElement,
  paragraphs: readonly TextParagraph[]
): { start: number; end: number } | null {
  const selection = root.ownerDocument.getSelection();
  if (!selection || selection.rangeCount === 0) return null;
  const anchor =
    selection.anchorNode && domPositionToOffset(root, paragraphs, selection.anchorNode, selection.anchorOffset);
  const focus =
    selection.focusNode && domPositionToOffset(root, paragraphs, selection.focusNode, selection.focusOffset);
  if (anchor == null || focus == null) return null;
  return { start: Math.min(anchor, focus), end: Math.max(anchor, focus) };
}

export function rangeToOffsets(
  root: HTMLElement,
  paragraphs: readonly TextParagraph[],
  range: StaticRange | Range
): { start: number; end: number } | null {
  const start = domPositionToOffset(root, paragraphs, range.startContainer, range.startOffset);
  const end = domPositionToOffset(root, paragraphs, range.endContainer, range.endOffset);
  return start == null || end == null ? null : { start: Math.min(start, end), end: Math.max(start, end) };
}

export function writeSelection(
  root: HTMLElement,
  paragraphs: readonly TextParagraph[],
  start: number,
  end: number = start
): void {
  const selection = root.ownerDocument.getSelection();
  if (!selection) return;
  const from = offsetToDomPosition(root, paragraphs, start);
  const to = offsetToDomPosition(root, paragraphs, end);
  const range = root.ownerDocument.createRange();
  range.setStart(from.node, from.offset);
  range.setEnd(to.node, to.offset);
  selection.removeAllRanges();
  selection.addRange(range);
}
